import { Injectable, OnDestroy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { SHARE_REPLAY_SETTINGS } from '@app/configuration.service';
import { SimpleData } from '@bazis/shared/models/srv.types';
import {
    shareReplay,
    startWith,
    map,
    tap,
    debounceTime,
    of,
    filter,
    merge,
    Observable,
} from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Injectable()
export class MassSelectedService implements OnDestroy {
    isSelectedOptions: SimpleData[] = [
        {
            id: '1',
            nameKey: '',
        },
    ];

    isSelectedAllOptions: SimpleData[] = [
        {
            id: '1',
            nameKey: 'action.selectAll',
        },
    ];

    isSelectedOptionsInTable: SimpleData[] = [
        {
            id: '1',
            name: '',
        },
    ];

    implicitlySelected = new Set();

    selectedForm = new FormGroup({});

    selectedFormChanges$ = this.selectedForm.valueChanges.pipe(shareReplay(SHARE_REPLAY_SETTINGS));

    selectedIds$ = this.selectedFormChanges$.pipe(
        startWith((form) => this.selectedForm.value),
        map((value) =>
            Object.keys(value).reduce((arr, current) => {
                if (value[current] && value[current][0] === '1') arr.push(current);
                return arr;
            }, []),
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    allSelected$ = this.selectedIds$.pipe(
        debounceTime(0),
        map((selectedIds) =>
            Array.from(new Set(selectedIds.concat(Array.from(this.implicitlySelected)))),
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    selectedAll = new FormControl([]);

    externalAllSource$;

    allowSelectAllChangeReaction = true;

    selectAllChanges$ = this.selectedAll.valueChanges.pipe(
        filter(() => {
            const allowNow = this.allowSelectAllChangeReaction;
            if (!allowNow) {
                this.allowSelectAllChangeReaction = true;
            }
            return allowNow;
        }),
        switchMap((value) => this.processSelectedAll$(value)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    updateSelectAllValue$ = this.allSelected$.pipe(
        switchMap((ids) => this.setSelectAllValue$(ids)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    isPartialSelection$: Observable<boolean> = this.allSelected$.pipe(
        debounceTime(0),
        switchMap((ids) => this.checkPartialSelection$(ids)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    essentialSubscriptions$ = merge(this.updateSelectAllValue$, this.selectAllChanges$);

    ngOnDestroy() {
        Object.keys(this.selectedForm).forEach((key) => {
            this.selectedForm.removeControl(key);
        });
        this.implicitlySelected.clear();
    }

    resetAllSelection() {
        Object.keys(this.selectedForm.controls).forEach((id) => {
            this.selectedForm.controls[id].setValue(0, { emitEvent: false });
        });

        this.implicitlySelected.clear();
        this.selectedForm.updateValueAndValidity();
    }

    selectIds(ids: string[]) {
        this.resetAllSelection();
        ids.forEach((id) => {
            if (this.selectedForm.controls[id]) {
                this.selectedForm.controls[id].setValue(['1']);
            } else {
                this.implicitlySelected.add(id);
            }
        });
    }

    updateForm(newSelectableIds, savePreviousSelection = true) {
        if (savePreviousSelection) {
            this.updateFormWithPreviousSelection(newSelectableIds);
        } else {
            this.updateFormWithoutPreviousSelection(newSelectableIds);
        }
    }

    updateFormWithPreviousSelection(newSelectableIds) {
        Object.keys(this.selectedForm.controls).forEach((id) => {
            if (newSelectableIds.indexOf(id) < 0) {
                if (
                    this.selectedForm.controls[id].value?.length > 0 &&
                    this.selectedForm.controls[id].value[0] === '1'
                ) {
                    this.implicitlySelected.add(id);
                }
                this.selectedForm.removeControl(id);
            }
        });

        newSelectableIds.forEach((id) => {
            if (!this.selectedForm.controls[id]) {
                this.selectedForm.addControl(id, new FormControl([]));
                if (this.implicitlySelected.has(id)) {
                    this.implicitlySelected.delete(id);
                    this.selectedForm.get(id).setValue(['1']);
                }
            }
        });
    }

    private updateFormWithoutPreviousSelection(newSelectableIds) {
        Object.keys(this.selectedForm.controls).forEach((id) => {
            if (newSelectableIds.indexOf(id) < 0) {
                this.selectedForm.removeControl(id);
            }
        });

        newSelectableIds.forEach((id) => {
            if (!this.selectedForm.controls[id]) {
                this.selectedForm.addControl(id, new FormControl([]));
            }
        });
    }

    private processSelectedAll$(value) {
        if (!this.externalAllSource$) {
            this.selectAllExisted(value);
            return of(null);
        }

        return this.externalAllSource$.pipe(
            tap((allIds: string[]) => {
                if (value.length > 0) {
                    allIds
                        .filter((id) => !this.selectedForm.controls[id])
                        .forEach((id) => {
                            this.implicitlySelected.add(id);
                        });
                } else {
                    allIds.forEach((id) => {
                        this.implicitlySelected.delete(id);
                    });
                }

                this.selectAllExisted(value);
            }),
            take(1),
        );
    }

    private setSelectAllValue$(ids) {
        if (!this.externalAllSource$) {
            if (
                ids.length > 0 &&
                ids.length === Object.keys(this.selectedForm.controls).length &&
                !this.selectedAll.value.length
            ) {
                this.setSelectedAllValue(['1']);
                return of(null);
            }
            if (
                (ids.length === 0 ||
                    ids.length !== Object.keys(this.selectedForm.controls).length) &&
                this.selectedAll.value.length === 1
            ) {
                this.setSelectedAllValue([]);
            }
            return of(null);
        }

        return this.externalAllSource$.pipe(
            tap((allIds: string[]) => {
                const allSelectedIds = new Set(ids);
                const isAllSelected = allIds.every((id) => allSelectedIds.has(id));

                if (isAllSelected && this.selectedAll.value.length === 0) {
                    this.setSelectedAllValue(['1']);
                    return;
                }
                if (!isAllSelected && this.selectedAll.value.length === 1) {
                    this.setSelectedAllValue([]);
                    return;
                }
            }),
        );
    }

    private checkPartialSelection$(selectedIds): Observable<boolean> {
        if (!selectedIds || this.selectedAll.value?.length > 0) {
            return of(false);
        }
        if (!this.externalAllSource$) {
            return of(selectedIds.findIndex((id) => this.selectedForm.value[id]?.length > 0) > -1);
        }

        return this.externalAllSource$.pipe(
            map((allIds: string[]) => {
                const allIdsSet = new Set(allIds);
                return selectedIds.findIndex((id) => allIdsSet.has(id)) > -1;
            }),
        );
    }

    selectAllExisted(value) {
        Object.keys(this.selectedForm.controls).forEach((id) => {
            this.selectedForm.controls[id].setValue([...value], { emitEvent: false });
        });
        this.selectedForm.updateValueAndValidity();
    }

    setSelectedAllValue(value) {
        this.allowSelectAllChangeReaction = false;
        this.selectedAll.setValue(value);
    }

    selectAll() {
        this.selectedAll.setValue(['1']);
    }

    removeFromSelected(selectedIds: string[]) {
        selectedIds.forEach((id) => {
            this.implicitlySelected.delete(id);
            if (this.selectedForm.get(id)) {
                this.selectedForm.removeControl(id, { emitEvent: false });
            }
        });
        this.selectedForm.updateValueAndValidity();
    }
}
